import * as Yup from 'yup';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { Trash, WarningTriangle } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';
import { Flex, TextV2, useDisclosure, useToast } from '@withjoy/joykit';
import { RichTextFormFields, RichTextFormProps } from '../../RichText.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import {
  Category,
  DesignLayoutType,
  FindHotelMatchQuery,
  TextComponentFragment,
  useAddTextComponentMutation,
  useFindHotelMatchLazyQuery,
  useUpdateTextComponentMutation,
} from '@graphql/generated';
import { CheckIcon } from '@assets/icons';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { CUSTOM_PAGE_ACTIONS } from '@website-editor/customComponents/CustomComponents.constants';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { RichTextEditor } from '@withjoy/joykit/components/RichTextEditor';
import { LayoutVirtualScrollableArea } from '@website-editor/features/custom-page/components/LeftPanelLayouts/LayoutVirtualScrollableArea';
import { useWebsiteEditorCustomPageTelemetry } from '@website-editor/features/custom-page/WebsiteEditorCustomPage.telemetry';
import { useEventDesignTheming } from '@apps/guest/routes/GuestSite/GuestSite.utils';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { BottomActions } from '@website-editor/features/custom-page/components/BottomActions/BottomActions';
import HotelSuggestionDialog from '../HotelSuggestionDialog/HotelSuggestionDialog';
import { customLinkPluginConfig } from '@withjoy/joykit/components/RichTextEditor/plugins/CustomLink/constants';
import { Jodit } from 'jodit-react';
import { getCustomLinkPluginControls, linkPopupRefDefaultValue, LinkPopupRef } from '@withjoy/joykit/components/RichTextEditor/plugins/CustomLink/helpers';
import { CustomCssDetectedDialog, useCustomCssPaste } from '@withjoy/joykit/components/RichTextEditor/plugins/CustomCssDetection';

export const RichTextForm: React.FC<RichTextFormProps> = props => {
  const { containerId, pageSlug, richTextData, isEditMode, isSingleComponent } = props;
  const { t } = useTranslation('websiteEditor');
  const richTextFormTrans = t('customPage', 'richText', 'form');
  const { toast } = useToast();
  const { replaceAndNavigateToScreen } = useNavigationPaneContext();
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const telemetry = useWebsiteEditorCustomPageTelemetry();
  const { eventDesign, setCustomComponentBlockData, setShowDeletePopup, setDeleteComponentDetails } = useCustomComponentStateContext<TextComponentFragment>();
  const { theme } = useEventDesignTheming({ eventDesign });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [hotelMatch, setHotelMatch] = useState<FindHotelMatchQuery['findHotelMatch'] | null>(null);
  const [editor, setEditor] = useState<Jodit | null>(null);
  const [currentLinkDetails, setCurrentLinkDetails] = useState<{ url: string; content: string; data?: string } | null>(null);
  const [isHotelLinkInterceptEnabled] = useIsFeatureEnabled('websiteInterceptExternalHotelLinksAdmin');
  const linkPopupRef = useRef<LinkPopupRef>(linkPopupRefDefaultValue);
  const { dialogProps: customCssDetectionDialogProps, customCssDetectionPlugin } = useCustomCssPaste();

  const [findHotelMatch] = useFindHotelMatchLazyQuery({
    onCompleted: result => {
      linkPopupRef.current?.close();
      linkPopupRef.current?.setLoading(false);
      if (result.findHotelMatch) {
        onOpen();
        setHotelMatch(result.findHotelMatch);
      } else {
        if (editor && currentLinkDetails) {
          editor.execCommand('applyCustomLink', false, { url: currentLinkDetails.url, content: currentLinkDetails.content, data: currentLinkDetails.data });
          setCurrentLinkDetails(null);
          setHotelMatch(null);
        }
      }
    },
    fetchPolicy: 'network-only',
    batchMode: 'fast',
  });

  const fontPackExperimentEnabled = useFeatureValue('fontPackExperiment').value === 'treatment';

  const [addTextComponent, { loading: addingTextComponent }] = useAddTextComponentMutation({
    onCompleted: result => {
      telemetry.websitePageComponentUpdated(pageSlug, 'Add', { action: 'added', id: result.addTextComponent.id, componentType: richTextData?.__typename ?? 'TextComponent' });
      toast(`${richTextFormTrans.saveComponentSuccess()}`, { icon: <CheckIcon /> });
      replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
    },
    onError: () => {
      toast(`${richTextFormTrans.saveComponentLimitError()}`, { icon: <WarningTriangle />, duration: 5000 });
    },
  });
  const [updateTextComponent, { loading: updatingTextComponent }] = useUpdateTextComponentMutation({
    onCompleted: result => {
      telemetry.websitePageComponentUpdated(pageSlug, 'Update', {
        action: 'updated',
        id: result.updateTextComponent.id,
        componentType: richTextData?.__typename ?? 'TextComponent',
      });
      toast(`${richTextFormTrans.saveComponentSuccess()}`, { icon: <CheckIcon /> });
      setCustomComponentBlockData({} as TextComponentFragment);
      replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
    },
    onError: () => {
      toast(`${richTextFormTrans.saveComponentError()}`, { icon: <WarningTriangle /> });
    },
  });
  const isLoading = addingTextComponent || updatingTextComponent;

  const formik = useFormik<RichTextFormFields>({
    initialValues: {
      text: richTextData?.text,
    },
    validationSchema: Yup.object({
      text: Yup.string().nullable().notRequired(),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: values => {
      if (isEditMode && richTextData?.id) {
        updateTextComponent({
          variables: { payload: { id: richTextData.id, sortOrder: richTextData.sortOrder, text: values.text ?? '' } },
          awaitRefetchQueries: true,
          refetchQueries: ['PageContainerBySlug'],
        }).then(() => {
          updateWebsitePreview();
        });
      } else {
        addTextComponent({
          variables: { payload: { containerId, sortOrder: richTextData?.sortOrder ?? 0, text: values.text ?? '' } },
          awaitRefetchQueries: true,
          refetchQueries: ['PageContainerBySlug'],
        }).then(() => {
          updateWebsitePreview();
        });
      }
    },
  });

  const handleCancel = useEventCallback(() => {
    setCustomComponentBlockData({} as TextComponentFragment);
    replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
  });

  const handleDeleteClick = useEventCallback(() => {
    if (richTextData?.id) {
      setShowDeletePopup(true);
      setDeleteComponentDetails({
        id: richTextData.id,
        type: richTextData.__typename,
        title: richTextFormTrans.deleteTitle(),
        subTitle: richTextFormTrans.deleteSubtitle(),
        deleteCta: richTextFormTrans.deleteCta(),
        cancelCta: richTextFormTrans.cancelCta(),
        successMessage: richTextFormTrans.deleteComponentSuccess(),
        errorMessage: richTextFormTrans.deleteComponentError(),
      });
    }
  });

  const fontMap = useMemo(() => {
    if (fontPackExperimentEnabled) {
      return eventDesign?.fonts?.reduce((acc, font) => ({ ...acc, ...{ [font.category]: font.font?.fontFamily } }), {} as Record<Category, string>);
    }
    return;
  }, [eventDesign?.fonts, fontPackExperimentEnabled]);

  const onSaveLink = useCallback(() => {
    onClose();
    editor?.async.requestIdleCallback(() => {
      if (editor && hotelMatch && currentLinkDetails) {
        editor.execCommand('applyCustomLink', false, { url: hotelMatch.link, content: currentLinkDetails.content, data: 'opted-in' });
        setCurrentLinkDetails(null);
        setHotelMatch(null);
      }
    });
  }, [editor, currentLinkDetails, hotelMatch, onClose]);

  const onKeepOriginalLink = useCallback(() => {
    onClose();
    editor?.async.requestIdleCallback(() => {
      if (editor && currentLinkDetails) {
        editor.execCommand('applyCustomLink', false, { url: currentLinkDetails.url, content: currentLinkDetails.content, data: 'opted-out' });
        setCurrentLinkDetails(null);
        setHotelMatch(null);
      }
    });
  }, [editor, currentLinkDetails, onClose]);

  const onAddLink = useCallback(
    (jodit: Jodit, url: string, content: string, data?: string | undefined) => {
      linkPopupRef.current?.setLoading(true);
      if (isHotelLinkInterceptEnabled && !data) {
        setEditor(jodit);
        setCurrentLinkDetails({ url, content, data });
        findHotelMatch({ variables: { url } });
      } else {
        linkPopupRef.current?.setLoading(false);
        linkPopupRef.current?.close();
        jodit.execCommand('applyCustomLink', false, { url, content, data });
      }
    },
    [isHotelLinkInterceptEnabled, findHotelMatch],
  );

  return (
    <>
      <form
        onSubmit={formik.handleSubmit}
        style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}
      >
        <LayoutVirtualScrollableArea>
          <Flex
            paddingX={[6, isSingleComponent ? 0 : 5]}
            paddingTop={8}
            paddingBottom={pxToRem(132)}
          >
            <Flex
              paddingX={[0, 6]}
              width="100%"
              flexDirection="column"
            >
              <Flex
                flexDirection="column"
                gap={7}
              >
                <RichTextEditor
                  content={formik.values.text}
                  handleChange={(value: string) => {
                    formik.setFieldValue('text', value);
                  }}
                  placeholder={richTextFormTrans.placeholder()}
                  {...customLinkPluginConfig}
                  customCssDetectionPlugin={customCssDetectionPlugin}
                  controls={getCustomLinkPluginControls(richTextFormTrans, linkPopupRef)}
                  customLinkPlugin={{
                    onAddLink,
                  }}
                  toolbarInlineForSelection={false}
                  theme={theme}
                  overrides={{
                    Paragraph: {
                      props: { typographyVariant: eventDesign?.websiteLayout.layoutType === DesignLayoutType.brannan ? 'body4' : 'body3', fontFamily: fontMap?.PARAGRAPH },
                    },
                    Heading: {
                      props: { fontFamily: fontMap?.HEADING },
                    },
                  }}
                />
                {isEditMode && (
                  <Flex
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Flex
                      gap={3}
                      cursor="pointer"
                      alignItems="center"
                      onClick={handleDeleteClick}
                    >
                      <Trash
                        size={24}
                        color="negative6"
                      />
                      <TextV2
                        typographyVariant="button2"
                        color="negative6"
                      >
                        {richTextFormTrans.deleteCta()}
                      </TextV2>
                    </Flex>
                  </Flex>
                )}
              </Flex>
            </Flex>
          </Flex>
        </LayoutVirtualScrollableArea>
        <BottomActions
          onCancel={handleCancel}
          onSave={() => formik.handleSubmit()}
          isLoading={isLoading}
          isSingleComponent={isSingleComponent}
        />
      </form>
      {customCssDetectionDialogProps.isOpen && <CustomCssDetectedDialog {...customCssDetectionDialogProps} />}
      {isOpen && (
        <HotelSuggestionDialog
          isOpen={isOpen}
          onClose={() => {
            onClose();
            setHotelMatch(null);
          }}
          onSaveLink={onSaveLink}
          onKeepOriginalLink={onKeepOriginalLink}
          hotelMatch={hotelMatch}
          links={{
            originalLink: currentLinkDetails?.url,
            suggestedLink: hotelMatch?.link,
          }}
        />
      )}
    </>
  );
};
